import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, MapPin } from "lucide-react";
import { BUSINESS, HERO } from "../data/content";
import { scrollToId } from "../lib/scroll";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden bg-royal-deep">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
        <img
          src={HERO.image}
          alt={HERO.alt}
          fetchPriority="high"
          className="w-full h-full object-cover object-[center_20%]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-royal-deep/85 via-royal-deep/50 to-black/30" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-royal-deep/80 via-transparent to-royal-deep/30" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full pt-28 pb-20">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-14 bg-gold" aria-hidden="true" />
            <span data-testid="hero-overline" className="text-gold text-xs md:text-sm tracking-[0.3em] uppercase font-medium">
              {HERO.overline}
            </span>
          </motion.div>

          <h1 data-testid="hero-heading" className="font-serif text-5xl sm:text-6xl lg:text-8xl text-ivory leading-[1.02] tracking-tight">
            {HERO.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-gold-light" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease }}
            className="mt-7 font-serif italic text-xl md:text-2xl text-gold/95"
          >
            {HERO.subheading}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.12, ease }}
            className="mt-3 text-base md:text-lg font-light text-ivory/75 max-w-xl leading-relaxed"
          >
            {HERO.supporting}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease }}
            className="mt-9 flex flex-wrap items-center gap-3.5"
          >
            <button
              data-testid="hero-explore-collections-button"
              onClick={() => scrollToId("collections")}
              className="px-7 py-3.5 bg-royal border border-gold/40 text-ivory text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-royal-light transition-colors duration-300"
            >
              Explore Our Collections
            </button>
            <button
              data-testid="hero-visit-store-button"
              onClick={() => scrollToId("contact")}
              className="px-7 py-3.5 border border-ivory/50 text-ivory text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-ivory hover:text-royal-deep transition-colors duration-300 flex items-center gap-2"
            >
              <MapPin size={15} strokeWidth={1.5} /> Visit Our Store
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={BUSINESS.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 bg-gold text-royal-deep text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-gold-light transition-colors duration-300 flex items-center gap-2"
            >
              <MessageCircle size={15} strokeWidth={1.5} /> WhatsApp Us
            </a>
          </motion.div>

          <motion.ul
            data-testid="hero-trust-highlights"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
          >
            {HERO.highlights.map((h) => (
              <li key={h} className="flex items-center gap-2.5 text-[13px] tracking-wide text-ivory/80">
                <span className="w-1.5 h-1.5 rotate-45 bg-gold" aria-hidden="true" />
                {h}
              </li>
            ))}
          </motion.ul>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-ivory/50">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
};
